import React from 'react';
import { Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
// Option A — FA5 fallback icons (the app is on @fortawesome/* 5.12.1). When we
// upgrade to Font Awesome 6, swap the five `icon:` values below to the exact
// icons in `fa6:` — this AREA_META block is the single place to change.
import {
  faTrafficLight,
  faIndustry,
  faTruckMoving,
  faSync,
  faPlug,
} from '@fortawesome/pro-regular-svg-icons';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  getThyroidPatternInsight,
  ThyroidPatternInsight,
  ThyroidArea,
} from '../../models/thyroid-pattern-insights.generated';
import { resolveThyroidPattern, ThyroidMarkerStates, Tri } from '../../models/ThyroidAnalysis';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');

const arrowStyle: React.CSSProperties = {
  height: '11px',
  margin: '0 3px',
  verticalAlign: 'middle',
  position: 'relative',
  top: '-0.5px',
};
const conditionTitleStyle: React.CSSProperties = { color: 'black', textAlign: 'left' };
const conditionListStyle: React.CSSProperties = { paddingLeft: 0, marginLeft: 0, listStyle: 'none' };
const bulletStyle: React.CSSProperties = { display: 'inline-block', marginRight: '3px' };
const markerStyle: React.CSSProperties = { color: 'black', textAlign: 'right' };
const sectionLabelStyle: React.CSSProperties = { textAlign: 'left', fontWeight: 600, color: 'black', marginTop: '6px' };

type Marker = { direction: 'up' | 'down'; label: string };

// Per-area likelihood band — frames each area as a likelihood of dysfunction on
// the same 4-zone palette as the gauge (green→yellow→orange→red); the icon +
// label use a darker, legible shade of the matching gauge zone (BAND_PALETTE) —
// the bright gauge colors are too light to read on the panel. `score` is the
// needle position (0–100) on the gauge:
//   • highlyLikely (red)    — a detected pattern belongs to this area.
//   • likely       (orange) — an area marker is outside the lab range, no pattern.
//   • possible     (yellow) — an area marker is outside optimal (within lab), no pattern.
//   • lessLikely   (green)  — area markers present, all within optimal.
//   • notAssessed  (gray)   — none of the area's markers were measured.
type Band = 'lessLikely' | 'possible' | 'likely' | 'highlyLikely' | 'notAssessed';

// Two palettes for the same four zones (green→red) plus an unassessed gray:
//  • GAUGE_PALETTE — the bright fills on the severity bars (the SAME colors as the
//    results-page marker bar graphs) and the legend gauge arcs.
//  • BAND_PALETTE  — darker, legible shades of those hues, used to tint the
//    likelihood word (the bright bar colors wash out as small text).
const GAUGE_PALETTE = {
  green: '#78F880',
  yellow: '#FEFA83',
  orange: '#FFD37E',
  red: '#FC6552',
  gray: '#e3e3e3',
};

const BAND_PALETTE = {
  green: '#3fa34d',
  yellow: '#c79a00', // gold-yellow
  orange: '#e3741a', // saturated orange — kept well clear of the gold above and the red below
  red: '#d8472f',
  gray: '#b7b7b7',
};

// Area icons are black; unassessed areas drop the icon to the muted
// BAND_PALETTE.gray so the row still reads as inactive.
const ICON_COLOR = '#000';

// `color` tints the legend chip; the row's likelihood label is a colored badge
// (`labelBg`/`labelFg`) styled like the MOO high/low labels — bright zone
// background with the matching dark/white text MOO uses.
const BAND_META: Record<
  Band,
  { label: string; color: string; labelBg: string; labelFg: string; score: number | null; meaning: string }
> = {
  lessLikely:   { label: 'less likely',   color: BAND_PALETTE.green,  labelBg: GAUGE_PALETTE.green,  labelFg: '#14521d', score: 25,   meaning: 'Markers tested, all within optimal.' },
  possible:     { label: 'possible',      color: BAND_PALETTE.yellow, labelBg: GAUGE_PALETTE.yellow, labelFg: '#2e2a00', score: 60,   meaning: 'A marker is outside optimal (still within lab range).' },
  likely:       { label: 'likely',        color: BAND_PALETTE.orange, labelBg: GAUGE_PALETTE.orange, labelFg: '#3d2700', score: 80,   meaning: 'A marker is outside the lab reference range.' },
  highlyLikely: { label: 'highly likely', color: BAND_PALETTE.red,    labelBg: GAUGE_PALETTE.red,    labelFg: '#fff',    score: 95,   meaning: 'A detected pattern points to this area.' },
  notAssessed:  { label: 'add markers',   color: BAND_PALETTE.gray,   labelBg: '#e6e6e6',           labelFg: '#777',    score: null, meaning: "This area's markers weren't tested yet." },
};

// Every row shows the same full 4-zone scale bar (green→yellow→orange→red, like
// the results-page reference charts); an X sits in the middle of the area's zone.
// BAND_ZONE_CENTER is that X's position as a % across the bar.
const BAR_COL_WIDTH = 110;
const BAR_HEIGHT = 14; // px — bar height; the zone marker dot matches this diameter
const BAND_ZONE_CENTER: Record<Band, number | null> = {
  lessLikely: 12.5,
  possible: 37.5,
  likely: 62.5,
  highlyLikely: 87.5,
  notAssessed: null,
};

// Legend display order (least → most likely, then not-assessed).
const LEGEND_ORDER: Band[] = ['lessLikely', 'possible', 'likely', 'highlyLikely', 'notAssessed'];

const AREA_ORDER: ThyroidArea[] = ['Signaling', 'Production', 'Transport', 'Conversion', 'Utilization'];

// Markers that inform each area's gauge (spec §10.2). An area is "assessed" if
// any of its markers were measured; its band = the worst deviation among them.
const AREA_MARKERS: Record<ThyroidArea, Array<keyof BloodworkAnalysis>> = {
  Signaling: ['tsh'],
  Production: ['t4Free', 't4Total', 't3Total', 'tpoAb', 'tgAb'],
  Transport: ['tbg', 't3Uptake'],
  Conversion: ['reverseT3'],
  Utilization: ['t3Free'],
};

// Centralized area metadata. `fa6` records the exact Font Awesome 6 icon to
// switch to after the @fortawesome upgrade (see spec §10.6).
// `iconSize` is an optional per-icon optical-size override (px). The traffic
// light and sync glyphs read visually larger than the others, so they render a
// touch smaller; everything else falls back to ICON_SIZE.
const ICON_SIZE = 24;
const AREA_META: Record<ThyroidArea, { label: string; icon: IconDefinition; fa6: string; iconSize?: number }> = {
  Signaling: { label: 'Signaling', icon: faTrafficLight, fa6: 'traffic-light', iconSize: 23 },
  Production: { label: 'Production', icon: faIndustry, fa6: 'industry-windows' },
  Transport: { label: 'Transport', icon: faTruckMoving, fa6: 'truck-moving' },
  Conversion: { label: 'Conversion', icon: faSync, fa6: 'arrows-rotate', iconSize: 22 },
  Utilization: { label: 'Utilization', icon: faPlug, fa6: 'plug-circle-bolt' },
};

// House display order for the per-pattern marker-arrow row.
const MARKER_ROW: Array<{ key: keyof BloodworkAnalysis; label: string }> = [
  { key: 'tsh', label: 'TSH' },
  { key: 't4Free', label: 'FT4' },
  { key: 't3Free', label: 'FT3' },
  { key: 't4Total', label: 'TT4' },
  { key: 't3Total', label: 'TT3' },
  { key: 'reverseT3', label: 'rT3' },
  { key: 'tbg', label: 'TBG' },
  { key: 't3Uptake', label: 'T3U' },
  { key: 'tpoAb', label: 'TPOAb' },
  { key: 'tgAb', label: 'TgAb' },
];

type Props = { bloodworkAnalysis: BloodworkAnalysis };

export class ThyroidRegulationInsights extends React.Component<Props, {}> {
  // ── Null-safe marker classification (mirrors GlucoseRegulationInsights) ─────
  markerIsHigh = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isGreaterThanOptimal?.());
  markerIsLow = (m: any) =>
    !!(m?.isLessThanLab?.() || m?.isStricklyLessThanOptimal?.() || m?.isLessThanOptimal?.());
  markerHasValue = (m: any) => !!m?.hasValue?.();
  // Granularity for the area likelihood ladder: outside the lab reference range
  // vs. merely outside optimal (still within lab).
  markerOutsideLab = (m: any) => !!(m?.isGreaterThanLab?.() || m?.isLessThanLab?.());
  markerOutsideOptimal = (m: any) =>
    !!(m?.isGreaterThanOptimal?.() || m?.isLessThanOptimal?.() || m?.isStricklyLessThanOptimal?.());

  triState = (m: any): Tri => {
    if (!this.markerHasValue(m)) return 'absent';
    if (this.markerIsHigh(m)) return 'high';
    if (this.markerIsLow(m)) return 'low';
    return 'opt';
  };

  buildStates = (ba: BloodworkAnalysis): ThyroidMarkerStates => ({
    tsh: this.triState(ba.tsh),
    ft4: this.triState(ba.t4Free),
    ft3: this.triState(ba.t3Free),
    rt3: this.triState(ba.reverseT3),
    tt4: this.triState(ba.t4Total),
    tt3: this.triState(ba.t3Total),
    tbg: this.triState(ba.tbg),
    t3u: this.triState(ba.t3Uptake),
    abPos: this.markerIsHigh(ba.tpoAb) || this.markerIsHigh(ba.tgAb),
  });

  buildMarkerArrows = (ba: BloodworkAnalysis): Marker[] => {
    const out: Marker[] = [];
    for (const { key, label } of MARKER_ROW) {
      const m = (ba as any)[key];
      if (this.markerIsHigh(m)) out.push({ direction: 'up', label });
      else if (this.markerIsLow(m)) out.push({ direction: 'down', label });
    }
    return out;
  };

  // ── 5-Area likelihood band ──────────────────────────────────────────────────
  // Ladder: detected pattern → highly likely; marker outside lab range → likely;
  // marker outside optimal (within lab) → possible; markers present & optimal →
  // less likely; no markers measured → not assessed.
  areaBand = (area: ThyroidArea, ba: BloodworkAnalysis, flaggedAreas: Set<ThyroidArea>): Band => {
    if (flaggedAreas.has(area)) return 'highlyLikely';
    const keys = AREA_MARKERS[area];
    if (!keys.some((key) => this.markerHasValue((ba as any)[key]))) return 'notAssessed';
    if (keys.some((key) => this.markerOutsideLab((ba as any)[key]))) return 'likely';
    if (keys.some((key) => this.markerOutsideOptimal((ba as any)[key]))) return 'possible';
    return 'lessLikely';
  };

  // ── Rendering ───────────────────────────────────────────────────────────────
  renderArrow = (direction: 'up' | 'down', label: string) => {
    const src = direction === 'up' ? arrow_up : arrow_down;
    return (
      <span style={{ whiteSpace: 'nowrap' }}>
        <img src={src} alt={direction === 'up' ? '↑' : '↓'} style={arrowStyle} />
        {label}
      </span>
    );
  };

  // Small semicircular dial. Needle position = the area's band score (green →
  // red); gray arc with no needle = not assessed.
  renderAreaGauge = (score: number | null) => {
    const polar = (v: number) => {
      const a = ((180 - 1.8 * v) * Math.PI) / 180; // v=0 → left, v=100 → right
      return { x: 32 + 26 * Math.cos(a), y: 32 - 26 * Math.sin(a) };
    };
    const arc = (v1: number, v2: number, color: string, key: string) => {
      const p1 = polar(v1);
      const p2 = polar(v2);
      return (
        <path
          key={key}
          d={`M ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A 26 26 0 0 1 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`}
          stroke={color}
          strokeWidth={7}
          fill="none"
        />
      );
    };
    const needle = score != null ? polar(score) : null;
    return (
      <svg width={72} height={45} viewBox="0 0 64 40" style={{ flexShrink: 0 }} aria-hidden="true">
        {score == null
          ? arc(0, 100, GAUGE_PALETTE.gray, 'gray')
          : [
              arc(0, 50, GAUGE_PALETTE.green, 'g'),
              arc(50, 70, GAUGE_PALETTE.yellow, 'y'),
              arc(70, 90, GAUGE_PALETTE.orange, 'o'),
              arc(90, 100, GAUGE_PALETTE.red, 'r'),
            ]}
        {needle && (
          <>
            <line x1={32} y1={32} x2={needle.x.toFixed(1)} y2={needle.y.toFixed(1)} stroke="#333" strokeWidth={1.5} />
            <circle cx={32} cy={32} r={2.2} fill="#333" />
          </>
        )}
      </svg>
    );
  };

  // ── "How to read this" legend (rendered inside the title's info Popover) ──────
  renderLikelihoodLegend = () => (
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '6px' }}>
        How likely each physiological area is involved.
      </div>
      {LEGEND_ORDER.map((band, i) => {
        const meta = BAND_META[band];
        return (
          <div
            key={band}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              padding: '5px 0',
              borderTop: i === 0 ? 'none' : '1px solid #f0f0f0',
            }}
          >
            {this.renderAreaGauge(meta.score)}
            <span
              style={{
                background: '#f5f5f5',
                borderRadius: '6px',
                padding: '3px 9px',
                fontSize: '12.5px',
                fontWeight: 700,
                color: meta.color,
                whiteSpace: 'nowrap',
                minWidth: '84px',
                textAlign: 'center',
              }}
            >
              {meta.label}
            </span>
            <span style={{ fontSize: '11.5px', color: '#555', lineHeight: 1.25 }}>{meta.meaning}</span>
          </div>
        );
      })}
    </div>
  );

  likelihoodLegendPopover = () => (
    <Popover id="thyroid-likelihood-legend" style={{ maxWidth: '340px' }}>
      <Popover.Title as="div" style={{ fontSize: '13px', fontWeight: 700, backgroundColor: '#fff', borderBottom: 'none' }}>
        5-Area Likelihood
      </Popover.Title>
      <Popover.Content>{this.renderLikelihoodLegend()}</Popover.Content>
    </Popover>
  );

  render5AreaMap = (flaggedAreas: Set<ThyroidArea>, ba: BloodworkAnalysis) => (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ textAlign: 'center', fontSize: '16px', fontWeight: 600, color: '#1a1a1a', marginBottom: '10px' }}>
        LabSmarts 5-Area Thyroid Framework&trade;
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {AREA_ORDER.map((area, index) => {
          const band = this.areaBand(area, ba, flaggedAreas);
          const meta = BAND_META[band];
          const iconColor = band === 'notAssessed' ? BAND_PALETTE.gray : ICON_COLOR;
          return (
            <div
              key={area}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '9px 2px',
                borderBottom: index < AREA_ORDER.length - 1 ? '1px solid #e6e6e6' : 'none',
              }}
            >
              {/* LEFT — icon + area name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', minWidth: 0 }}>
                <FontAwesomeIcon
                  icon={AREA_META[area].icon}
                  style={{ fontSize: `${AREA_META[area].iconSize ?? ICON_SIZE}px`, width: '28px', color: iconColor }}
                />
                <span style={{ fontSize: '17px', fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>
                  {AREA_META[area].label}
                </span>
              </div>
              {/* RIGHT — full 4-zone scale bar with an X in the area's zone, word beneath */}
              <div style={{ width: BAR_COL_WIDTH, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {band === 'notAssessed' ? (
                  <>
                    <div style={{ height: `${BAR_HEIGHT}px`, borderRadius: '3px', background: '#e6e6e6' }} />
                    <span style={{ marginTop: '3px', fontSize: '12px', fontStyle: 'italic', color: BAND_PALETTE.gray, lineHeight: 1 }}>
                      not assessed
                    </span>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        position: 'relative',
                        height: `${BAR_HEIGHT}px`,
                        borderRadius: '3px',
                        background: `linear-gradient(90deg, ${GAUGE_PALETTE.green} 0 25%, ${GAUGE_PALETTE.yellow} 25% 50%, ${GAUGE_PALETTE.orange} 50% 75%, ${GAUGE_PALETTE.red} 75% 100%)`,
                      }}
                    >
                      {/* zone divider lines — same gray as the reference charts' gridlines */}
                      {[25, 50, 75].map((p) => (
                        <span key={p} style={{ position: 'absolute', top: 0, bottom: 0, left: `${p}%`, width: '1px', background: '#CDD7EA' }} />
                      ))}
                      {/* diamond marks the middle of the area's zone (solid = current reading) */}
                      <span
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: `${BAND_ZONE_CENTER[band]}%`,
                          transform: 'translate(-50%, -50%) rotate(45deg)',
                          width: '9px',
                          height: '9px',
                          background: '#1a1a1a',
                        }}
                      />
                    </div>
                    <div style={{ position: 'relative', height: '18px', marginTop: '4px' }}>
                      <span
                        style={{
                          position: 'absolute',
                          top: 0,
                          ...(band === 'lessLikely'
                            ? { left: 0 }
                            : band === 'highlyLikely'
                            ? { right: 0 }
                            : { left: `${BAND_ZONE_CENTER[band]}%`, transform: 'translateX(-50%)' }),
                          fontSize: '12px',
                          fontWeight: 600,
                          color: meta.labelFg,
                          background: meta.labelBg,
                          borderRadius: '3px',
                          padding: '2px 7px',
                          whiteSpace: 'nowrap',
                          lineHeight: 1,
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  renderBulletSection = (label: string, items: string[]) => (
    <div style={{ textAlign: 'left' }}>
      <div style={sectionLabelStyle}>{label}</div>
      <ul style={conditionListStyle}>
        {items.map((item) => (
          <li key={item} style={{ display: 'flex', alignItems: 'flex-start' }}>
            <span style={bulletStyle}>&bull;</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  renderPatternBox = (insight: ThyroidPatternInsight, markers: Marker[]) => (
    <div className="condition-section">
      <div style={{ ...conditionTitleStyle, fontWeight: 700, fontSize: '14px' }}>{insight.title}</div>
      {insight.bullets.length > 0 && this.renderBulletSection('Consistent with:', insight.bullets)}
      {markers.length > 0 && (
        <div style={markerStyle}>
          {markers.map((marker, index) => (
            <React.Fragment key={`${marker.direction}-${marker.label}-${index}`}>
              {index > 0 && <>,{' '}</>}
              {this.renderArrow(marker.direction, marker.label)}
            </React.Fragment>
          ))}
        </div>
      )}
      {insight.rootCauses.length > 0 && this.renderBulletSection('Possible Root Causes:', insight.rootCauses)}
    </div>
  );

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis.bloodwork) return null;
    const ba = bloodworkAnalysis;

    // Render only if at least one thyroid marker was measured.
    const hasAnyThyroidMarker = MARKER_ROW.some(({ key }) => this.markerHasValue((ba as any)[key]));
    if (!hasAnyThyroidMarker) return null;

    // Single source of truth: the same pure resolver the model uses.
    const states = this.buildStates(ba);
    const categoryId = resolveThyroidPattern(states);
    const insight = categoryId != null ? getThyroidPatternInsight(categoryId) : undefined;

    const flaggedAreas = new Set<ThyroidArea>();
    if (insight) flaggedAreas.add(insight.area);

    const markers = this.buildMarkerArrows(ba);

    return (
      <Card className="evaluationPanel" id="thyroid-regulation-insights">
        <Card.Header className="evaluationPanelHeading">
          Thyroid Insights
          <OverlayTrigger placement="bottom" overlay={this.likelihoodLegendPopover()}>
            <FontAwesomeIcon
              icon="info-circle"
              style={{ cursor: 'pointer', color: '#fff', fontSize: 14, marginLeft: 8, verticalAlign: 'middle' }}
            />
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {this.render5AreaMap(flaggedAreas, ba)}
            {insight && this.renderPatternBox(insight, markers)}
          </div>
        </Card.Body>
      </Card>
    );
  }
}
